import {
  ConsoleTransport,
  type ILogLayer,
  LogLayer,
  type PluginBeforeMessageOutParams,
} from 'loglayer';

/** Fields accumulated by `withError`/`withMetadata` before a level call emits. */
type Attached = {
  error?: unknown;
  metadata?: Record<string, unknown>;
};

/**
 * Edge Runtime does not support Node.js fs APIs, and development uses the
 * console shim below: the pretty-terminal transport it replaced reached the
 * `date-fns` root barrel and cost ~1240 webpack modules per dev compile.
 */
const useConsoleLogger =
  process.env['NEXT_RUNTIME'] === 'edge' ||
  process.env['NODE_ENV'] === 'development';

// Detect if we're on the server or client
const isServer = typeof window === 'undefined';

// ANSI color codes — no extra dependency needed
const COLORS = {
  debug: '\x1b[36m', // cyan
  info: '\x1b[32m', // green
  warn: '\x1b[33m', // yellow
  error: '\x1b[31m', // red
  reset: '\x1b[0m',
  dim: '\x1b[2m',
} as const;

type ColorLevel = 'debug' | 'info' | 'warn' | 'error';

function colorize(level: string, message: string): string {
  const color = COLORS[level as ColorLevel] ?? '';
  const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
  return `${COLORS.dim}${now}${COLORS.reset} ${color}${level.toUpperCase().padEnd(5)}${COLORS.reset} ${message}`;
}

function createLogLayerLogger(): LogLayer {
  const logLevel = (process.env['LOG_LEVEL'] ?? 'info') as
    | 'trace'
    | 'debug'
    | 'info'
    | 'warn'
    | 'error'
    | 'fatal';

  const transport = new ConsoleTransport({
    level: logLevel,
    enabled: true,
    logger: console,
  });

  const log = new LogLayer({
    transport: [transport],
    plugins: [
      {
        // Add a plugin to label the log entry as coming from the server or client
        onBeforeMessageOut(params: PluginBeforeMessageOutParams) {
          const tag = isServer ? 'Server' : 'Client';

          if (params.messages && params.messages.length > 0) {
            if (typeof params.messages[0] === 'string') {
              params.messages[0] = `[${tag}] ${params.messages[0]}`;
            }
          }

          return params.messages;
        },
      },
    ],
  });

  return log;
}

/**
 * Console-backed stand-in for LogLayer. It must carry the whole surface the app
 * calls — `withError` and `withMetadata` chain into a level call — and it tags
 * the entry the way the LogLayer plugin does.
 */
function createConsoleLogger(attached: Attached = {}): ILogLayer {
  const emit =
    (level: ColorLevel, write: (...args: unknown[]) => void) =>
    (message: string, ...rest: unknown[]) => {
      const tag = isServer ? 'Server' : 'Client';
      const parts: unknown[] = [
        colorize(level, `[${tag}] ${message}`),
        ...rest,
      ];
      if (attached.metadata) {
        parts.push(attached.metadata);
      }
      if (attached.error) {
        parts.push(attached.error);
      }
      write(...parts);
    };

  const shim = {
    debug: emit('debug', (...args) => console.debug(...args)),
    info: emit('info', (...args) => console.info(...args)),
    warn: emit('warn', (...args) => console.warn(...args)),
    error: emit('error', (...args) => console.error(...args)),
    log: emit('info', (...args) => console.log(...args)),
    withError: (error: unknown) => createConsoleLogger({ ...attached, error }),
    withMetadata: (metadata: Record<string, unknown>) =>
      createConsoleLogger({
        ...attached,
        metadata: { ...attached.metadata, ...metadata },
      }),
    withContext: (context: Record<string, unknown>) =>
      createConsoleLogger({
        ...attached,
        metadata: { ...attached.metadata, ...context },
      }),
  };
  return shim as unknown as ILogLayer;
}

export const logger: ILogLayer = useConsoleLogger
  ? createConsoleLogger()
  : createLogLayerLogger();
